import type { LngLat } from '../shared/types';

const R = 6371008.8; // 地球半径 [m]
const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;

/** 2点間の距離 [m]（haversine） */
export function distance(a: LngLat, b: LngLat): number {
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** a→b の方位角 [度, 北=0 時計回り, 0..360) */
export function bearing(a: LngLat, b: LngLat): number {
  const φ1 = toRad(a[1]);
  const φ2 = toRad(b[1]);
  const Δλ = toRad(b[0] - a[0]);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** 2つの方位角の差（-180..180） */
export function angleDiff(a: number, b: number): number {
  return ((b - a + 540) % 360) - 180;
}

/** a と b の間を t (0..1) で線形補間（短距離前提） */
export function lerp(a: LngLat, b: LngLat, t: number): LngLat {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

/**
 * 点 p を線分 a-b に投影する。短距離なので局所的な平面近似（経度方向を cos(緯度) で補正）。
 * 戻り値 t は 0..1 にクランプ済み。
 */
export function projectToSegment(p: LngLat, a: LngLat, b: LngLat): { point: LngLat; t: number } {
  const k = Math.cos(toRad(p[1]));
  const ax = a[0] * k, ay = a[1];
  const bx = b[0] * k, by = b[1];
  const px = p[0] * k, py = p[1];
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return { point: lerp(a, b, t), t };
}

/** ポリラインの累積距離 [m]（cum[i] = 始点から頂点 i までの距離） */
export function cumulativeDistances(line: LngLat[]): number[] {
  const cum = [0];
  for (let i = 1; i < line.length; i++) cum.push(cum[i - 1] + distance(line[i - 1], line[i]));
  return cum;
}
